import { AnalyticsProvider } from "@pandas/analytics";
import { DEFAULT_PARTNER, getLanguageCode, languageDirection } from "@pandas/core-ui";
import { canonicalizePartnerRoute } from "@pandas/core-ui/route";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Loader from "@/components/Loaders/Loader";
import ErrorPopup from "@/components/Modals/ErrorPopup";
import { servicesOrchestratorValues } from "@/config/services";
import { ApplicationRequirements } from "@/features/application/pages/ApplicationRequirements";
import { StepProvider } from "@/features/assessments/pages/step-provider/StepProvider";
import { assessmentEngine } from "@/lib/assessments/assessmentEngineSingleton";
import { appInitializer } from "@/lib/flow-logic/appInitializer";
import { setErrorMessage } from "@/lib/flow-logic/getErrorMessage";
import logger from "@/lib/logging/logger";
import { StepManagerProvider } from "@/lib/StepManagerContext";
import { useStore } from "@/store";
import i18next from "../../../i18n";
import { initSearchSchema } from "./-search-params";

export const Route = createFileRoute("/$locale/$partner")({
	validateSearch: (search) => initSearchSchema.parse(search),
	beforeLoad: async ({ context, search, params, location }) => {
		const result = await canonicalizePartnerRoute({
			pathname: location.pathname,
			search,
			locale: params.locale,
			currentPartnerSegment: params.partner,
			reservedPartnerSegments: context.reservedPartnerRouteSegments,
			resolvePartner: (s) => (typeof s.partnerCode === "string" ? s.partnerCode : null),
		});

		if (result?.kind === "redirect") throw redirect({ href: result.href, replace: true });

		if (result?.kind === "unresolvable") {
			throw redirect({
				to: "/$locale/$partner",
				params: { locale: params.locale, partner: DEFAULT_PARTNER },
				replace: true,
			});
		}

		const isAppInitializing = useStore.getState().isAppInitializing;
		const partnerPath = `/${params.locale}/${params.partner}`;
		const storeLocationsPath = `${partnerPath}/store-locations`;

		if (isAppInitializing && location.pathname !== partnerPath && location.pathname !== storeLocationsPath) {
			throw redirect({
				to: "/$locale/$partner",
				params: { locale: params.locale, partner: params.partner },
				search,
				replace: true,
			});
		}

		// Return search for loader context
		return { search };
	},
	loader: async ({ params, context }) => {
		const isAppInitializing = useStore.getState().isAppInitializing;
		const isAssessmentEngineReady = Boolean(assessmentEngine);
		if (!isAppInitializing && isAssessmentEngineReady) return;

		try {
			await appInitializer(params.partner, context.search, i18next);
		} catch (e) {
			setErrorMessage(e, "Error during app initialization.");
		}
	},
	pendingComponent: Loader,
	component: PartnerLayout,
});

function PartnerLayout() {
	const { i18n } = useTranslation();
	const { flow } = Route.useSearch();

	const error = useStore((state) => state.error);
	const errorType = typeof error === "string" ? error : "INTEGRATION_ERROR";
	const initializedScreens = useStore((state) => state.initializedScreens);

	useEffect(() => {
		logger.injectCustomProp("url", window.location.href);
	}, []);

	const { posthogKey, posthogHost, axiom } = servicesOrchestratorValues.credentials;
	const isVisionAiFlow = flow === "vision-ai" && posthogKey && posthogHost;

	const content = (
		<StepManagerProvider screens={initializedScreens}>
			<StepProvider>
				<div
					dir={languageDirection(getLanguageCode(i18n.language))}
					className="h-[calc(var(--vh)+env(safe-area-inset-top))] flex flex-col items-center justify-center w-full bg-concrete"
				>
					<Outlet />
					{Boolean(error) && <ErrorPopup errorType={errorType} />}
					<ApplicationRequirements />
				</div>
			</StepProvider>
		</StepManagerProvider>
	);

	if (isVisionAiFlow) {
		return (
			<AnalyticsProvider
				providers={["posthog"]}
				environment={servicesOrchestratorValues.environment}
				project="webapp"
				posthog={{
					apiKey: posthogKey,
					host: posthogHost,
					browserOptions: { capture_pageview: false, disable_session_recording: true },
				}}
				axiom={{
					token: axiom.token,
					orgId: axiom.orgId,
					dataset: axiom.dataset,
					environment: servicesOrchestratorValues.environment,
					project: "webapp",
				}}
			>
				{content}
			</AnalyticsProvider>
		);
	}

	return content;
}
